import { useEffect, useState } from "react";
import UsersApi from "../../api/usersApi";
import Loader from "../../components/Loader/Loader";
import { Badge } from "../../components/ui/badge";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { ScrollArea } from "../../components/ui/scroll-area";
import { SpecialistData } from "../../types/TypeSpecialist";
import { TypeUserData } from "../../types/TypeUsers";
import { FieldChange, FieldReviewState } from "../../utils/profileDiffUtils";
import { compareProfileFields } from "../../utils/profileFieldConfig";
import { ChangeItemEnhanced } from "./components/ChangeItemEnhanced";

interface ProfileDiffViewProps {
  userId: string;
  currentUser: TypeUserData;
  currentSpecialistInfo: SpecialistData;
  specializationLabel?: string; // E.g., "Coach", "Therapist", "Mentor"
  onFieldReviewChange?: (fieldName: string, state: FieldReviewState) => void;
  onChangesLoaded?: (totalCount: number) => void;
}

// Note: specialistId is currentSpecialistInfo._id - the ID of the specific specialization being reviewed

export const ProfileDiffView = ({
  userId,
  currentUser,
  currentSpecialistInfo,
  specializationLabel,
  onFieldReviewChange,
  onChangesLoaded,
}: ProfileDiffViewProps) => {
  const [isLoading, setIsLoading] = useState(true);
  const [publicData, setPublicData] = useState<TypeUserData | null>(null);
  const [publicSpecialistInfo, setPublicSpecialistInfo] =
    useState<SpecialistData | null>(null);
  const [changes, setChanges] = useState<FieldChange[]>([]);
  const [fieldReviews, setFieldReviews] = useState<
    Map<string, FieldReviewState>
  >(new Map());

  useEffect(() => {
    loadPendingProfile();
  }, [userId, currentSpecialistInfo._id]);

  const loadPendingProfile = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem("token");
      if (!token) return;

      // CRITICAL: getUserProfileInfoById returns the PENDING (updated/under review) profile
      // NOT the public/live profile as the name might suggest
      const response = await UsersApi.getUserProfileInfoById(token, userId);

      if (response.status && response.user) {
        setPublicData(response.user); // TODO: Rename this to pendingData for clarity

        // CRITICAL FIX: Find the matching specialization by ID, not just [0]
        const pendingSpecializations = Array.isArray(
          response.user.specialistIds
        )
          ? response.user.specialistIds
          : response.user.specialistId
          ? [response.user.specialistId]
          : [];

        // Match by _id to ensure we're comparing the SAME specialization
        const pendingSpec = pendingSpecializations.find(
          (spec) => spec._id === currentSpecialistInfo._id
        );

        if (!pendingSpec) {
          console.warn(
            "Could not find matching pending specialization for ID:",
            currentSpecialistInfo._id
          );
          setPublicSpecialistInfo(null);
          setChanges([]);
          return;
        }

        setPublicSpecialistInfo(pendingSpec); // TODO: Rename to pendingSpecialistInfo

        // Compare: PUBLIC (from props) vs PENDING (from API)
        const detectedChanges = detectChanges(
          response.user,
          pendingSpec,
          currentSpecialistInfo
        );
        setChanges(detectedChanges);

        // Notify parent of total changes count
        if (onChangesLoaded) {
          onChangesLoaded(detectedChanges.length);
        }
      }
    } catch (error) {
      console.error("Error loading pending profile:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const detectChanges = (
    pendingUserFromAPI: TypeUserData,
    pendingSpecFromAPI: SpecialistData,
    publicSpecFromProps: SpecialistData
  ): FieldChange[] => {
    // CRITICAL: Data sources are:
    // - currentUser (prop) = PUBLIC/live user data from review queue
    // - currentSpecialistInfo (prop) = PUBLIC/live specialization from review queue
    // - getUserProfileInfoById API = PENDING/updated data (what user submitted for review)
    //
    // Comparison should be: OLD (public) → NEW (pending)

    const detectedChanges = compareProfileFields(
      currentUser, // OLD: Public user (live)
      publicSpecFromProps, // OLD: Public specialization (live)
      pendingUserFromAPI, // NEW: Pending user (under review)
      pendingSpecFromAPI, // NEW: Pending specialization (under review)
      userId
    );

    // CRITICAL BUG FIX #2: If reviewRequested is true but no changes detected,
    // show a generic message so reviewers know WHY this is in the queue
    if (detectedChanges.length === 0 && pendingSpecFromAPI.reviewRequested) {
      return [
        {
          field: "reviewRequested",
          label: "Review Requested",
          oldValue: "No review requested",
          newValue: "Review requested by specialist",
          category: "professional",
          severity: "minor",
          changeType: "modified",
        },
      ];
    }

    return detectedChanges;
  };

  const handleFieldReview = (fieldName: string, state: FieldReviewState) => {
    setFieldReviews((prev) => {
      const newMap = new Map(prev);
      newMap.set(fieldName, state);
      return newMap;
    });

    // Notify parent component with field label included
    if (onFieldReviewChange) {
      // Find the change to get the human-readable label
      const change = changes.find((c) => c.field === fieldName);
      const fieldLabel = change?.label || fieldName;

      // Pass enriched state with label
      onFieldReviewChange(fieldLabel, state);
    }
  };

  if (isLoading) {
    return (
      <div className="dark flex items-center justify-center h-full bg-gray-900">
        <Loader size={60} />
      </div>
    );
  }

  if (!publicData || !publicSpecialistInfo) {
    return (
      <div className="dark flex items-center justify-center h-full bg-gray-900">
        <p className="text-gray-400">Unable to load comparison data</p>
      </div>
    );
  }

  const personalChanges = changes.filter((c) => c.category === "personal");
  const professionalChanges = changes.filter(
    (c) => c.category === "professional"
  );
  const servicesChanges = changes.filter((c) => c.category === "services");

  return (
    <div className="dark h-full bg-gray-900">
      <div className="bg-gray-800 border-b border-gray-700 p-4">
        <h2 className="text-xl font-semibold text-white mb-2">
          Profile Changes Comparison
          {specializationLabel && (
            <span className="text-blue-400 ml-2">({specializationLabel})</span>
          )}
        </h2>
        <div className="flex items-center gap-2">
          <Badge variant="secondary">
            {changes.length} {changes.length === 1 ? "Change" : "Changes"}{" "}
            Detected
          </Badge>
          {currentSpecialistInfo && (
            <Badge variant="outline" className="text-xs">
              ID: {currentSpecialistInfo._id}
            </Badge>
          )}
        </div>
      </div>

      <ScrollArea className="h-[calc(100%-80px)]">
        <div className="p-6 space-y-6">
          {changes.length === 0 ? (
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="pt-6">
                <p className="text-center text-gray-400">No changes detected</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {personalChanges.length > 0 && (
                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader>
                    <CardTitle className="text-base text-white">
                      Personal Information Changes
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {personalChanges.map((change, idx) => (
                      <ChangeItemEnhanced
                        key={idx}
                        change={change}
                        userId={userId}
                        specialistId={currentSpecialistInfo._id}
                        mainSpecId={
                          currentSpecialistInfo.mainSpecializations[0]._id
                        }
                        subSpecializations={
                          currentSpecialistInfo.subSpecializations
                        }
                        reviewState={fieldReviews.get(change.field)}
                        onReviewChange={(state) =>
                          handleFieldReview(change.field, state)
                        }
                      />
                    ))}
                  </CardContent>
                </Card>
              )}

              {professionalChanges.length > 0 && (
                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader>
                    <CardTitle className="text-base text-white">
                      Professional Information Changes
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {professionalChanges.map((change, idx) => (
                      <ChangeItemEnhanced
                        key={idx}
                        change={change}
                        userId={userId}
                        specialistId={currentSpecialistInfo._id}
                        mainSpecId={
                          currentSpecialistInfo.mainSpecializations[0]._id
                        }
                        subSpecializations={
                          currentSpecialistInfo.subSpecializations
                        }
                        reviewState={fieldReviews.get(change.field)}
                        onReviewChange={(state) =>
                          handleFieldReview(change.field, state)
                        }
                      />
                    ))}
                  </CardContent>
                </Card>
              )}

              {servicesChanges.length > 0 && (
                <Card className="bg-gray-800 border-gray-700">
                  <CardHeader>
                    <CardTitle className="text-base text-white">
                      Service Configuration Changes
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {servicesChanges.map((change, idx) => (
                      <ChangeItemEnhanced
                        key={idx}
                        change={change}
                        userId={userId}
                        specialistId={currentSpecialistInfo._id}
                        mainSpecId={
                          currentSpecialistInfo.mainSpecializations[0]._id
                        }
                        subSpecializations={
                          currentSpecialistInfo.subSpecializations
                        }
                        reviewState={fieldReviews.get(change.field)}
                        onReviewChange={(state) =>
                          handleFieldReview(change.field, state)
                        }
                      />
                    ))}
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};
